// A streamed Mac window's chrome, as the headset draws it (docs/wireframe/visionos-screens.md §8; MacWindows.swift
// `MacWindowView`). The picture is NOT painted here: frames arrive 30–60 times a second and this painter redraws
// the whole window on every change, so the app keeps the picture on its own plane (its canvas and texture, as
// today) and puts this window's plane a millimetre in front of it. This window is transparent where the picture
// is (`pictureRect()`), and draws only what sits on or around it and changes rarely: the top ornament (the control
// bar), the centred note, the dictation line, the Pointer menu, the tooltips.
//
// Layout, in points: a band `BAND` tall on top holding the ornament (visionOS hangs it above the window's top edge,
// its bottom 6 pt above), then the window itself, `picW × picH`, whose proportions are the stream's (WindowAspect:
// the window resizes uniformly, minimum width 400). When the ornament is wider than a narrow window the plane widens
// and the window stays centred under it.
//
// Around it: an extension's parts and its ⋯ (stream-parts.js), and the app's shortcuts — the pinned strip on the
// trailing side, the Shortcuts sheet, gestures on the title (../../shortcuts/stream-shortcuts.js).
//
// One seam: `new MacWindow(ctx)`, with `ctx.stream` the app's player for this window (see fixtures/macwindow.js):
//   stream.window            the WindowInfo being streamed {id, app, title, width, height}
//   stream.aspect            width ÷ height from the Mac's 'P' (format), null until then
//   stream.canControl        'P'.canControl
//   stream.covered           'S'.covered
//   stream.idle              untouched for 90 s with Trickle on: streaming at 5 fps
//   stream.note, stream.noteSymbol, stream.reconnectIn   what the picture's note says (MacWindowPlayer's strings)
//   stream.listening, stream.transcript, stream.speechProblem   dictation
//   stream.start(prefs)      open the stream with these choices ('O' {id, maxFPS, options})
//   stream.apply(prefs)      the choices changed ('C' options; idle drop; how input is aimed)
//   stream.send(event)       an InputEvent ('I'); here only {action: 'raise'} (Bring Forward)
//   stream.touched()         a touch: leave the idle trickle ('C' with the full rate)
//   stream.reconnectNow()    skip the countdown
//   stream.toggleDictation() start / stop listening
//   stream.softKeyboard      a phone or tablet: the bar has a keyboard button (streamkeys.js)
//   stream.keyboardUp        its soft keyboard is up, typing to this window
//   stream.toggleKeyboard()  raise / lower it (inside the tap, so the browser allows it)
//   stream.pasteHere()       type this device's clipboard into the Mac's focused field (Cmd-V pastes the Mac's own)
//   stream.picture           preview only: a canvas to show where the picture goes. The app leaves it unset.
// and `ctx.settings` (the choices are remembered per app per Mac, like StreamPrefs), `ctx.open('home')`.
// The app asks: `pictureRect()` (where to put the picture plane), `takes(u, v)` (does the chrome take the pointer
// here, or does it go to the Mac), `fit(width)` (a uniform resize from the corner handle).
import { Win, esc, icon, addSymbols } from '../../core/kit.js'
import { StreamParts } from './stream-parts.js'
import { StreamShortcuts } from '../../shortcuts/stream-shortcuts.js'
import { StreamHints } from './stream-hints.js'

addSymbols({
  'rectangle.stack': 'layers',
  'moon.zzz': 'moon',
  'cursorarrow.motionlines': 'mouse-pointer',
  'arrow.up.and.down': 'arrow-up-down',
  cursorarrow: 'mouse-pointer-2',
  'character.cursor.ibeam': 'text-cursor',
  macwindow: 'app-window',
  'cursorarrow.click': 'mouse-pointer-click',
  eye: 'eye',
  mic: 'mic',
  'mic.fill': 'mic',
  waveform: 'audio-lines',
  lock: 'lock',
  'dot.radiowaves.left.and.right': 'radio',
  'info.circle': 'info',
  'arrow.clockwise': 'rotate-cw',
  keyboard: 'keyboard',
  'keyboard.chevron.compact.down': 'keyboard-off',
  'doc.on.clipboard': 'clipboard-paste',
})

export const BAND = 56          // the ornament's band above the window
const MIN_W = 400, IDLE_FPS = 5
const DEFAULTS = { aim: 'pointer', pointerStyle: 'headset', forwardsPointer: true, scrollMode: false, staysBehind: false, maxFPS: 30, idleDrop: true }
const FPS = [[10, '10 fps — reading'], [15, '15 fps'], [30, '30 fps — usual'], [60, '60 fps — video, games']]
const sym = (s, colour) => `<img class="sym" src="${icon(s, colour)}">`

export default class MacWindow extends Win {
  constructor(ctx) {
    const s = ctx.stream || {}
    const ratio = s.aspect || (s.window?.width || 1000) / Math.max(s.window?.height || 700, 1)
    const picW = 1000, picH = Math.round(picW / ratio)
    super(ctx, { cls: 'macwin', w: picW, h: BAND + picH, glass: false })
    this.picW = picW; this.picH = picH; this.ratio = ratio
    this.prefs = this.loadPrefs()
    this.anchor = { left: 0, top: BAND }
    this.watchHover()
    this.xs = new StreamParts(this)   // an extension's parts around this window, and its ⋯ (stream-parts.js)
    this.sc = new StreamShortcuts(this)   // the app's shortcuts: the strip beside it, the sheet, gestures on the title
    this.hs = new StreamHints(this)   // Hints: labels on what can be pressed — said, typed or tapped (stream-hints.js)
    s.start?.({ ...this.prefs })
  }

  get stream() { return this.ctx.stream || {} }

  // MARK: the choices, remembered per app per Mac (StreamPrefs)

  prefsKey() { return `stream.prefs.${this.ctx.macID || this.ctx.mac || 'mac'}.${this.stream.window?.app || ''}` }
  loadPrefs() {
    const saved = this.ctx.settings?.get(this.prefsKey(), null)
    return { ...DEFAULTS, ...(saved && typeof saved === 'object' ? saved : {}) }
  }
  setPref(key, value) {
    this.prefs = { ...this.prefs, [key]: value }
    this.ctx.settings?.set(this.prefsKey(), this.prefs)
    this.stream.apply?.({ ...this.prefs })
  }

  // MARK: for the app

  /// Where the window (the picture) is on this plane, in points from its top left.
  pictureRect() {
    // with an extension's parts around it the picture moves: where it is laid out
    const pic = (this.xs?.cached || this.sc?.shown) && this.root.querySelector('.picture')
    if (pic) {
      const r = pic.getBoundingClientRect(), box = this.root.getBoundingClientRect(), k = box.width ? this.size.w / box.width : 1
      if (r.width) return { x: Math.round((r.left - box.left) * k), y: Math.round((r.top - box.top) * k), w: this.picW, h: this.picH }
    }
    return { x: Math.round((this.size.w - this.picW) / 2), y: BAND, w: this.picW, h: this.picH }
  }

  /// Whether the chrome takes the pointer at (u, v) — a control, the note, an open menu (a tap anywhere closes it) —
  /// rather than the Mac.
  takes(u, v) {
    if (this.menu || this.sc?.open || [...(this.xs?.pss.values() || [])].some((p) => p.confirm)) return true
    const box = this.root.getBoundingClientRect()
    const x = box.left + u * box.width, y = box.top + v * box.height
    for (const el of this.root.querySelectorAll('.ornament, .note, .listen, .menu, .xs-part, .sc-strip, .hint, .hzone, .hbar')) {
      const r = el.getBoundingClientRect()
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return true
    }
    return false
  }

  /// A uniform resize: the window takes this width (at least 400) and the stream's proportions.
  fit(width) {
    this.picW = Math.max(MIN_W, Math.round(width))
    this.picH = Math.round(this.picW / this.ratio)
    this.render()
  }

  // MARK: drawing

  draw() {
    const s = this.stream, p = this.prefs
    const w = s.window || { app: '', title: '' }
    const above = this.xs.above()   // first: it reads the extension's doc for the whole drawing
    const parts = [`<button class="ob icon" data-act="home:" aria-label="Home" data-help="Open the Home window">${sym('house')}</button>`,
      `<span class="otitle"${this.sc.barAttrs()}>${esc(`${w.app} — ${w.title}`)}</span>`, this.xs.menuButton()]
    // whenever it is covered (Josh 10-01: only the note showed) — the Mac ignores it if control is off there
    if (s.covered) parts.push(`<button class="ob" data-act="raise:" data-help="Another window covers this one on the Mac, so its app may have stopped drawing. This brings it to the front.">${sym('rectangle.stack')}<span>Bring Forward</span></button>`)
    if (s.idle) parts.push(`<button class="ob" data-act="wake:" data-help="Untouched for a while, so it streams at ${IDLE_FPS} fps to save bandwidth. Tap to bring the rate back; 'Trickle when untouched' in the Pointer menu turns this off for this window.">${sym('moon.zzz')}<span>Idle · ${IDLE_FPS} fps</span></button>`)
    if (s.canControl) {
      const on = (b) => (b ? ' on' : '')
      const ink = (b) => (b ? 'var(--c-control-on-ink)' : 'var(--c-ink)')
      parts.push(`<button class="ob${on(p.scrollMode)}" data-act="scroll:" data-help="What a pinch-and-drag does: move the pointer, or scroll. To drag or select: tap, then press again and hold, and move. Hold still for half a second = right-click. A tap right next to a pointer you just placed clicks at the pointer.">${sym(p.scrollMode ? 'arrow.up.and.down' : 'cursorarrow.motionlines', ink(p.scrollMode))}<span>${p.scrollMode ? 'Scroll' : 'Move'}</span></button>`)
      parts.push(`<button class="ob${on(p.forwardsPointer)}" data-act="hover:" data-help="Move the Mac's pointer with your mouse or trackpad. Turn off if it fights with the Mac's own trackpad.">${sym('cursorarrow', ink(p.forwardsPointer))}<span>Mouse hover</span></button>`)
      parts.push(`<button class="ob${this.menu === 'pointer' ? ' open' : ''}" data-act="menu:pointer" data-help="Which pointer you see: one drawn on the headset the moment you move (always an arrow), or the Mac's own, captured in the picture (I-beam, hand… but it only redraws when the window does)">${sym(p.pointerStyle === 'headset' ? 'cursorarrow' : 'macwindow')}<span>Pointer</span></button>`)
      parts.push(this.hs.button())
      if (s.softKeyboard) parts.push(`<button class="ob${on(s.keyboardUp)}" data-act="keyboard:" data-help="Type into the Mac window: each key goes to the Mac as you press it. The strip above the keyboard has Esc, Tab, the arrows, Cmd and Ctrl.">${sym(s.keyboardUp ? 'keyboard.chevron.compact.down' : 'keyboard', ink(s.keyboardUp))}<span>Keyboard</span></button>`)
      parts.push(`<button class="ob" data-act="pastehere:" data-help="Types what this device has copied into the focused field on the Mac. Cmd-V pastes the Mac's own clipboard.">${sym('doc.on.clipboard')}<span>Paste from Here</span></button>`)
      parts.push(`<button class="ob${s.listening ? ' red' : ''}" data-act="dictate:" data-help="Dictate into the focused text field on the Mac">${sym(s.listening ? 'mic.fill' : 'mic', s.listening ? 'var(--c-red)' : 'var(--c-ink)')}<span>${s.listening ? 'Stop' : 'Dictate'}</span></button>`)
    } else if (!s.note) {
      parts.push(`<span class="viewonly" data-act="noop:" data-help="To click and type: Cody McCodeface on the Mac → Sharing › Windows → “…and click, scroll and type in them”">${sym('eye', 'rgb(var(--c-ink-rgb) / 0.55)')}<span>view only</span></span>`)
    }
    const band = `<div class="band"><div class="ornament" style="max-width:${this.picW}px">${parts.join('')}</div></div>`

    const note = s.note
      ? `<div class="center"><div class="note">${sym(s.noteSymbol || 'info.circle')}<span class="nt">${esc(s.note)}</span>${s.reconnectIn != null ? '<button class="btn bordered small" data-act="now:">Now</button>' : ''}</div></div>`
      : ''
    const listen = s.listening || s.speechProblem
      ? `<div class="lwrap"><div class="listen">${sym(s.speechProblem ? 'exclamationmark.triangle' : 'waveform')}<span class="lt">${esc(s.speechProblem || (s.transcript ? s.transcript : 'Listening… (tap a text field in the window first)'))}</span></div></div>`
      : ''
    // on a phone or a tablet (core/render-dom.js) the picture is as wide as the screen, in the window's proportions
    const size = this.ctx.screen ? `width:100%;aspect-ratio:${this.picW}/${this.picH}` : `width:${this.picW}px;height:${this.picH}px`
    const picture = `<div class="picture${s.picture ? ' filled' : ''}" style="${size}"><div class="slot"></div>${this.hs.overlay()}${note}${listen}</div>`
    const strip = this.sc.strip()   // the pinned shortcuts, on the trailing side
    const middle = this.xs.sides() || strip ? `<div class="xs-mid">${this.xs.leading()}${picture}${this.xs.trailing()}${strip}</div>` : picture
    return band + above + middle + this.xs.below() + this.pointerMenu() + this.xs.overlay() + this.sc.overlay()
  }

  /// Laid out for a screen, or back to the headset's size (core/render-dom.js).
  screenChanged(screen) { const on = !!screen; if (on !== this.onScreen) { this.onScreen = on; this.render() } }

  /// The Pointer menu: the stream's options, as the headset lists them.
  pointerMenu() {
    if (this.menu !== 'pointer') return ''
    const p = this.prefs
    const row = (act, label, checked, symbol) => `<button class="mi" data-act="${act}">${checked ? sym('checkmark') : '<span class="sym"></span>'}<span class="ml">${esc(label)}</span>${symbol ? `<span class="spacer"></span>${sym(symbol)}` : ''}</button>`
    const app = this.stream.window?.app || 'this app'
    const body = [
      '<div class="msec">Pointer</div>',
      row('pref:pointerStyle=headset', 'Drawn on the headset — instant', p.pointerStyle === 'headset', 'cursorarrow'),
      row('pref:pointerStyle=mac', "The Mac's own pointer — real shape, can lag", p.pointerStyle === 'mac', 'macwindow'),
      '<div class="msec">A pinch clicks</div>',
      row('pref:aim=pointer', 'At the pointer — drag it there first', p.aim === 'pointer', 'cursorarrow.click'),
      row('pref:aim=look', 'Where you look', p.aim === 'look', 'eye'),
      '<div class="div"></div>',
      '<div class="msec">Frame rate</div>',
      ...FPS.map(([n, label]) => row('pref:maxFPS=' + n, label, p.maxFPS === n)),
      row('pref:idleDrop=' + !p.idleDrop, 'Trickle when untouched', p.idleDrop, 'moon.zzz'),
      `<div class="mtext">After a minute and a half without a touch the stream drops to ${IDLE_FPS} fps to save bandwidth; the next touch brings the rate straight back. Turn off for a window you only watch.</div>`,
      '<div class="div"></div>',
      row('pref:staysBehind=' + !p.staysBehind, 'Keep the app behind on the Mac', p.staysBehind, 'rectangle.stack'),
      `<div class="mtext">Clicks and typing go to the app directly, without bringing its window forward — what you are doing on the Mac keeps its focus. Some apps ignore input that did not come with focus; turn it off for those. Remembered for ${esc(app)} on this Mac.</div>`,
    ].join('')
    const top = this.anchor.top + 8
    const left = Math.max(8, Math.min(this.anchor.left, this.size.w - 448))
    return `<div class="menu pmenu" data-scroll="pointer" style="left:${left}px;top:${top}px;max-height:${this.size.h - top - 10}px">${body}</div>`
  }

  after() {
    // the picture's canvas, in the preview only (the app keeps its picture on its own plane)
    const slot = this.root.querySelector('.slot')
    if (this.stream.picture && slot) slot.appendChild(this.stream.picture)
    // the stream's proportions, once known (WindowAspect): keep the width, set the height
    const ratio = this.stream.aspect || this.ratio
    if (ratio && Math.abs(ratio - this.ratio) > 0.001) {
      this.ratio = ratio
      this.picH = Math.round(this.picW / ratio)
      const pic = this.root.querySelector('.picture')
      if (pic) { if (this.ctx.screen) pic.style.aspectRatio = this.picW + '/' + this.picH; else pic.style.height = this.picH + 'px' }
    }
    // the plane: the window, widened when the ornament is wider than it
    // (the title gives way first, down to 80 pt; past that the plane grows, as an ornament may be wider than its window)
    const orn = this.root.querySelector('.ornament')
    const need = orn ? Math.ceil(orn.scrollWidth) : 0
    if (orn && need > this.picW) orn.style.maxWidth = need + 'px'
    // an extension's bands add height; its side columns (and a wide band) add width
    const bands = [...this.root.querySelectorAll('.xs-band')]
    const mid = this.root.querySelector('.xs-mid')
    const extW = Math.max(mid ? Math.ceil(mid.scrollWidth) : 0, ...bands.map((b) => Math.ceil(b.firstElementChild?.scrollWidth || 0) + 16))
    const extH = bands.reduce((n, b) => n + Math.ceil(b.offsetHeight), 0)
    // an open Shortcuts sheet has its least room, however small the window
    const min = this.sc.minSize()
    const w = Math.max(this.picW, need + 8, extW, min?.w || 0), h = Math.max(BAND + this.picH + extH, min?.h || 0)
    if (w !== this.size.w || h !== this.size.h) {
      this.resize(w, h)
      if (this.menu) this.render()   // the menu's room follows the plane
    }
    this.sc.after()
  }

  act(kind, arg, el, e) {
    const s = this.stream
    const ext = this.xs.act(kind, arg, el, e)
    if (ext !== undefined) return ext
    const hint = this.hs.act(kind, arg)
    if (hint !== undefined) return hint
    const sc = this.sc.act(kind, arg, el)
    if (sc !== undefined) return sc
    if (kind === 'noop') return false
    if (kind === 'home') { this.ctx.open('home'); return false }
    if (kind === 'raise') { s.send?.({ action: 'raise' }); return false }
    if (kind === 'wake') { s.touched?.(); return }
    if (kind === 'now') { s.reconnectNow?.(); return }
    if (kind === 'dictate') { s.toggleDictation?.(); return }
    if (kind === 'keyboard') { s.toggleKeyboard?.(); return }
    if (kind === 'pastehere') { s.pasteHere?.(); return false }
    if (kind === 'scroll') { this.setPref('scrollMode', !this.prefs.scrollMode); return }
    if (kind === 'hover') { this.setPref('forwardsPointer', !this.prefs.forwardsPointer); return }
    if (kind === 'pref') {
      const [key, raw] = arg.split('=')
      const value = raw === 'true' ? true : raw === 'false' ? false : key === 'maxFPS' ? Number(raw) : raw
      this.setPref(key, value)
    }
  }

  /// The menu opens under its button: remember where the button is before the kit toggles it.
  onClick(e) {
    const el = e.target.closest?.('[data-act="menu:pointer"]')
    if (el) {
      const r = el.getBoundingClientRect(), box = this.root.getBoundingClientRect()
      this.anchor = { left: Math.round(r.left - box.left), top: Math.round(r.bottom - box.top) }
    }
    this.hideTip()
    super.onClick(e)
  }

  // MARK: .help — a tooltip under what the pointer has rested on for a second (§16), added straight to the DOM so
  // the rest of the window is not rebuilt

  watchHover() {
    this.hoverWatch = new MutationObserver(() => {
      const el = this.root.querySelector('.hover[data-help], [data-help].hover')
      if (el === this.tipFor) return
      this.hideTip()
      this.tipFor = el
      if (el) this.tipTimer = setTimeout(() => this.showTip(el), 1000)
    })
    this.hoverWatch.observe(this.root, { attributes: true, attributeFilter: ['class'], subtree: true })
  }
  showTip(el) {
    if (!el.isConnected || this.menu) return
    const r = el.getBoundingClientRect(), box = this.root.getBoundingClientRect()
    const tip = document.createElement('div')
    tip.className = 'tip'
    tip.textContent = el.dataset.help
    const width = Math.min(360, this.size.w - 16)
    tip.style.width = width + 'px'
    tip.style.left = Math.max(8, Math.min(r.left - box.left + r.width / 2 - width / 2, this.size.w - width - 8)) + 'px'
    tip.style.top = (r.bottom - box.top + 8) + 'px'
    this.root.appendChild(tip)
    this.tip = tip
  }
  hideTip() { clearTimeout(this.tipTimer); this.tip?.remove(); this.tip = null; this.tipFor = null }

  typed(key, value) { if (this.hs.typed(key, value)) return; if (!this.sc.typed(key, value)) this.xs.typed(key, value) }
  /// Return in a field: the Hints label field's words go to the Mac.
  submit(key, value) { this.hs.submit(key, value) }
  /// A long press: a gesture on the title, or a shortcut's context menu (false: the press goes on as a tap).
  hold(kind, arg) { return this.hs.hold(kind, arg) ?? this.sc.hold(kind, arg) }

  dispose() { this.hideTip(); this.hoverWatch?.disconnect(); this.xs.dispose(); this.sc.dispose(); this.hs.dispose(); super.dispose() }
}
